import { useMemo } from 'react';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import { InsightsQueryParams } from 'components/insights/query/useInsightsAnalytics';
import { useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';

import { isRealDimensionValue } from '../constants';
import useInboundReferralAnalytics from '../query/useInboundReferralAnalytics';

export type SelectedFacilityExtreme = { name: string; rate: number };

// Side query for the "Top / Least Selected SNF" tiles: the facility with the highest and lowest
// selected rate, always by facility regardless of the breakdown's active View By. One query serves
// both tiles — the Selected drill reads `most`, the Not Selected drill reads `least`. Dashboard-
// scoped (metric drills add no entity filter). Enabled as soon as the slideout is open (it doesn't
// depend on the main query) and returns the raw fetch state, so the tile shows spinner -> value
// instead of flashing an em-dash first.
export default function useInboundSelectedFacilityExtremes(): {
  most: SelectedFacilityExtreme | null;
  least: SelectedFacilityExtreme | null;
  loading: boolean;
} {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);

  // closed_count rides along as the volume guard (only rank facilities that reached an outcome) and
  // as a concrete count metric — a rate-only query doesn't populate the series client-side.
  const params = useMemo<InsightsQueryParams>(
    () => ({
      source: Sources.REFERRAL_INTAKES,
      dimensions: [Dimensions.FACILITY],
      metrics: [Metrics.SELECTED_RATE, Metrics.CLOSED_COUNT],
    }),
    []
  );

  const { data, loading } = useInboundReferralAnalytics(params, {
    enabled: !!slideoutContext,
    queryKey: 'inbound-slideout-selected-facility-extremes',
  });

  return useMemo(() => {
    const names = data.dimensions.getCurrentDimensionValuesByIndex(0);
    const rates = data.metrics.getCurrentSeries(Metrics.SELECTED_RATE);
    const closed = data.metrics.getCurrentSeries(Metrics.CLOSED_COUNT);

    const facilities: SelectedFacilityExtreme[] = [];
    names.forEach((name, i) => {
      const rate = rates[i];
      if (!isRealDimensionValue(name) || rate == null || (closed[i] ?? 0) <= 0) return;
      facilities.push({ name, rate });
    });

    const most = facilities.reduce<SelectedFacilityExtreme | null>((m, f) => (!m || f.rate > m.rate ? f : m), null);
    const least = facilities.reduce<SelectedFacilityExtreme | null>((m, f) => (!m || f.rate < m.rate ? f : m), null);
    return { most, least, loading };
  }, [data, loading]);
}
